// Pure helpers behind the Firewall screen (mockup 05): diff flags, labels, the tag vocabulary,
// editor hints and the client-side mirror of the draft's validation (CONTRACT 3.3 PolicyRule).
import type { Device, PolicyDiff, PolicyEndpoint, PolicyProtocol, PolicyRule, PolicyStatus } from '../../api/types';
import { formatAgo, formatDateTime } from '../../lib/format';

/** The id GET /policy/status reports the pinned "deny everything else" row under. */
export const DEFAULT_RULE_ID = 'default';

export interface RuleHits {
  /** Matches in the last 24 h; null when the relay has no counter for the rule. */
  count: number | null;
  /** "1,284"; an en dash without a counter. */
  hits: string;
  /** "2 min ago", "never"; an en dash without a counter. */
  lastMatch: string;
  /** The absolute time of the last match, for a tooltip; null without one. */
  lastMatchAt: string | null;
}

const thousands = new Intl.NumberFormat('en');

/**
 * A row's Hits 24 h and Last match, joined from GET /policy/status by rule id (`default` for the
 * default row). A rule the relay has no counter for (new in the draft, not published, or the
 * status not loaded) reads as dashes, not as zero.
 */
export function ruleHits(status: PolicyStatus | null, ruleId: string, now: number = Date.now()): RuleHits {
  const counter = status?.rules.find((r) => r.ruleId === ruleId);
  if (!counter) return { count: null, hits: '–', lastMatch: '–', lastMatchAt: null };
  return {
    count: counter.hits24h,
    hits: thousands.format(counter.hits24h),
    lastMatch: formatAgo(counter.lastMatchAt, now),
    lastMatchAt: counter.lastMatchAt,
  };
}

/** The editor's "1,284 matches in 24 h"; "no counter yet" when the relay has none for the rule. */
export function hitsText(h: Pick<RuleHits, 'count'>): string {
  if (h.count === null) return 'no hit counter yet';
  return `${thousands.format(h.count)} ${h.count === 1 ? 'match' : 'matches'} in 24 h`;
}

/** The tooltip on a Last match cell. */
export const lastMatchTitle = (h: Pick<RuleHits, 'lastMatchAt'>) => (h.lastMatchAt ? formatDateTime(h.lastMatchAt) : undefined);

export type RowChange = 'added' | 'changed' | 'moved' | 'none';

/** How a draft row differs from the active version; added beats changed beats moved. */
export function rowChange(ruleId: string, diff: PolicyDiff | null): RowChange {
  if (!diff) return 'none';
  if (diff.added.some((r) => r.id === ruleId)) return 'added';
  if (diff.changed.some((c) => c.id === ruleId)) return 'changed';
  if (diff.moved.some((m) => m.id === ruleId)) return 'moved';
  return 'none';
}

/** "any" reads as "Any" (the mockup's Meraki vocabulary); every other endpoint is shown verbatim. */
export function formatEndpoint(e: PolicyEndpoint | string): string {
  return e === 'any' ? 'Any' : e;
}

/** A port list as written ("445,5001"), "Any" for any. */
export function formatPorts(ports: string): string {
  return ports === 'any' ? 'Any' : ports;
}

export function formatProtocol(p: PolicyProtocol): string {
  return p === 'any' ? 'Any' : p.toUpperCase();
}

export interface TagUsage {
  tag: string;
  devices: number;
  rules: number;
}

const tagsOf = (r: PolicyRule) =>
  [r.source, r.destination].filter((e) => e.startsWith('tag:')).map((e) => e.slice(4));

/**
 * Every tag on a device or in a rule, with how many devices carry it and how many rules name it.
 * Tags rules use come first (most rules first, then in the order the policy first names them);
 * then the unused ones by name - the "no rules" rows the mockup flags.
 */
export function tagVocabulary(devices: Device[], rules: PolicyRule[]): TagUsage[] {
  const firstUse = new Map<string, number>();
  const ruleCount = new Map<string, number>();
  rules.forEach((r, i) => {
    for (const t of new Set(tagsOf(r))) {
      if (!firstUse.has(t)) firstUse.set(t, i);
      ruleCount.set(t, (ruleCount.get(t) ?? 0) + 1);
    }
  });
  const all = new Set([...devices.flatMap((d) => d.tags), ...ruleCount.keys()]);
  return [...all]
    .map((tag) => ({ tag, devices: devices.filter((d) => d.tags.includes(tag)).length, rules: ruleCount.get(tag) ?? 0 }))
    .sort((a, b) => {
      if (!a.rules !== !b.rules) return a.rules ? -1 : 1;
      if (a.rules) return b.rules - a.rules || firstUse.get(a.tag)! - firstUse.get(b.tag)!;
      return a.tag.localeCompare(b.tag);
    });
}

/** The enrolled devices an endpoint covers, for the editor's "2 devices match …" hint. */
export function matchingDevices(endpoint: string, devices: Device[]): Device[] {
  if (endpoint === 'any') return devices;
  const [kind, value] = [endpoint.slice(0, endpoint.indexOf(':')), endpoint.slice(endpoint.indexOf(':') + 1)];
  switch (kind) {
    case 'tag':
      return devices.filter((d) => d.tags.includes(value));
    case 'net':
      return devices.filter((d) => d.network === value);
    case 'device':
      return devices.filter((d) => d.id === value);
    default:
      return [];
  }
}

/** "2 devices match", "1 device matches", "no device matches". */
export function matchText(n: number): string {
  if (n === 0) return 'no device matches';
  return n === 1 ? '1 device matches' : `${n} devices match`;
}

const TAG_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;
const NET_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;
const DEVICE_RE = /^dev_[0-9a-f]{12}$/;

export function endpointError(e: string): string | null {
  if (e === 'any') return null;
  if (e.startsWith('tag:')) return TAG_RE.test(e.slice(4)) ? null : 'A tag is lowercase letters, digits and dashes.';
  if (e.startsWith('net:')) return NET_RE.test(e.slice(4)) ? null : 'Not a network id.';
  if (e.startsWith('device:')) return DEVICE_RE.test(e.slice(7)) ? null : 'A device id is dev_ and 12 hex digits.';
  return 'Use any, tag:<tag>, net:<network> or device:<id>.';
}

/** "any" or up to 16 comma-separated ports / a-b ranges in 1..65535 (a < b). */
export function portsError(ports: string, protocol: PolicyProtocol): string | null {
  if (ports === 'any') return null;
  if (protocol !== 'tcp' && protocol !== 'udp') return 'Ports only apply to TCP or UDP.';
  const items = ports.split(',');
  if (items.length > 16) return 'At most 16 ports or ranges.';
  const port = (s: string) => /^\d{1,5}$/.test(s) && Number(s) >= 1 && Number(s) <= 65535;
  for (const item of items) {
    const [a, b, ...rest] = item.split('-');
    if (rest.length || !a || !port(a) || (b !== undefined && (!port(b) || Number(a) >= Number(b)))) {
      return `"${item}" is not a port or a-b range in 1..65535.`;
    }
  }
  return null;
}

export function commentError(comment: string): string | null {
  if (comment.length > 100) return 'At most 100 characters.';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(comment)) return 'No control characters.';
  return null;
}

export type RuleErrors = Partial<Record<'source' | 'destination' | 'destinationPorts' | 'sourcePorts' | 'comment', string>>;

/** The checks the draft PUT makes per rule, so the editor can stop a save the server would refuse. */
export function validateRule(r: PolicyRule): RuleErrors {
  const errors: RuleErrors = {};
  const set = (k: keyof RuleErrors, msg: string | null) => {
    if (msg) errors[k] = msg;
  };
  set('source', endpointError(r.source));
  set('destination', endpointError(r.destination));
  set('sourcePorts', portsError(r.sourcePorts, r.protocol));
  set('destinationPorts', portsError(r.destinationPorts, r.protocol));
  set('comment', commentError(r.comment));
  return errors;
}

/** Moves the rule at `from` to `to` (0-based), leaving the input untouched. */
export function moveRule(rules: PolicyRule[], from: number, to: number): PolicyRule[] {
  const next = [...rules];
  const [r] = next.splice(from, 1);
  if (r) next.splice(Math.max(0, Math.min(to, next.length)), 0, r);
  return next;
}

/** A new rule as "Add rule" opens it: the server assigns the id on save. */
export function blankRule(): PolicyRule {
  return {
    id: '',
    action: 'allow',
    protocol: 'tcp',
    source: 'any',
    sourcePorts: 'any',
    destination: 'any',
    destinationPorts: 'any',
    comment: '',
    enabled: true,
  };
}
